import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import {
  Sparkles, Gauge, ChevronRight, GraduationCap, Globe2, BookOpen, Mic, ChevronLeft,
  Headphones, PencilLine, Volume2, Brain, Timer, Target, ShieldCheck,
} from "lucide-react";

/**
 * ExamChoice — single unified entry point for every exam test.
 * Cleanly presents the two practice modes the student can pick:
 *  1. Practice with AI Guidelines  (hybrid — text hints by default, voice on demand)
 *  2. Practice Mock Test           (strict timed simulation)
 *
 * Daylight Cream HUD theme.
 */
const T = {
  bg: "#FCFAF3",
  cardBg: "#FFFFFF",
  border: "#E5DCC0",
  borderSoft: "#EEE6CE",
  primary: "#F77F00",
  primaryDark: "#D26900",
  primaryGlow: "rgba(247,127,0,0.45)",
  text: "#003049",
  textMuted: "rgba(0,48,73,0.65)",
  accentBlue: "#0C4B66",
};

const EXAM_META = {
  IELTS: {
    name: "IELTS",
    fullName: "International English Language Testing System",
    Icon: Globe2,
    color: "#0C4B66",
    bands: "Bands 1.0 – 9.0",
    duration: "≈ 2h 45m",
    skills: ["Listening", "Reading", "Writing", "Speaking"],
    routes: {
      ai: "/app/ielts/guidelines",
      mock: "/app/ielts/start",
    },
    aiBlurb:
      "Sarah, our IELTS tutor, walks you through each module — format, timing, dos & don'ts, smart strategies — then you practise with live AI hints on every question.",
    mockBlurb:
      "Realistic Paper-based or Computer-based simulation. 30-minute Listening, 60-minute Reading, 60-minute Writing. AI-graded with full band score.",
  },
  PTE: {
    name: "PTE Academic",
    fullName: "Pearson Test of English",
    Icon: Mic,
    color: "#6B21A8",
    bands: "Score 10 – 90",
    duration: "≈ 2 hours",
    skills: ["Speaking", "Writing", "Reading", "Listening"],
    routes: {
      ai: "/app/pte",
      mock: "/app/pte/mock",
    },
    aiBlurb:
      "AI tutor explains every PTE task type — Read Aloud, Describe Image, Repeat Sentence, Re-tell Lecture — with model answers and hints while you practise.",
    mockBlurb:
      "Full PTE simulation — AI-scored within seconds. Same task order as the official test, same time limits, same scoring criteria.",
  },
  DUO: {
    name: "Duolingo English Test",
    fullName: "Duolingo Adaptive English Test",
    Icon: BookOpen,
    color: "#15803D",
    bands: "Score 10 – 160",
    duration: "≈ 1 hour",
    skills: ["Reading", "Writing", "Listening", "Speaking"],
    routes: {
      ai: "/app/duolingo",
      mock: "/app/duolingo/mock",
    },
    aiBlurb:
      "Practise every Duolingo task type — Read & Complete, Read Aloud, Listen & Type, Speak Photo, Interactive Reading — with adaptive AI hints.",
    mockBlurb:
      "Adaptive on-demand mock test. Difficulty rises as you answer correctly, just like the real Duolingo test.",
  },
  ELLT: {
    name: "Oxford ELLT",
    fullName: "Oxford English Language Level Test",
    Icon: GraduationCap,
    color: "#1E3A8A",
    bands: "CEFR A1 – C2",
    duration: "≈ 1h 30m",
    skills: ["Reading", "Writing", "Listening", "Speaking"],
    routes: {
      ai: "/app/oxford",
      mock: "/app/oxford/mock",
    },
    aiBlurb:
      "Step-by-step prep for the Oxford ELLT — Reading MCQ, written response and recorded speaking — recognised by 100+ UK universities.",
    mockBlurb:
      "Full Oxford ELLT mock — strict timing, written and spoken responses, AI-graded against the CEFR descriptors.",
  },
};

const SKILL_ICON = {
  Listening: Headphones,
  Reading: BookOpen,
  Writing: PencilLine,
  Speaking: Mic,
};

const playClick = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "triangle";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.16, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.14);
    o.start(); o.stop(ctx.currentTime + 0.15);
  } catch (_) { /* ignore */ }
};

export default function ExamChoice() {
  const { examId } = useParams();
  const nav = useNavigate();
  const meta = EXAM_META[examId] || EXAM_META.IELTS;
  const Icon = meta.Icon;
  const [hover, setHover] = useState(null);

  const go = (path) => {
    playClick();
    setTimeout(() => nav(path), 120);
  };

  return (
    <DashboardLayout>
      <div className="w-full max-w-6xl mx-auto" data-testid={`exam-choice-${examId}`}>
        {/* Breadcrumb */}
        <Link
          to="/app"
          className="inline-flex items-center gap-1.5 text-xs font-mono tracking-[0.25em] uppercase mb-6 hover:text-[#F77F00] transition"
          style={{ color: T.textMuted }}
          data-testid="exam-choice-back"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back to dashboard
        </Link>

        {/* Exam banner */}
        <div
          className="relative overflow-hidden rounded-sm p-7 md:p-9 mb-8"
          style={{
            background: `linear-gradient(135deg, ${meta.color} 0%, #003049 100%)`,
            color: "#fff",
            boxShadow: `0 24px 60px -32px ${meta.color}cc`,
          }}
        >
          {/* corner brackets */}
          <span className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2" style={{ borderColor: T.primary }} />
          <span className="absolute top-3 right-3 w-5 h-5 border-t-2 border-r-2" style={{ borderColor: T.primary }} />
          <span className="absolute bottom-3 left-3 w-5 h-5 border-b-2 border-l-2" style={{ borderColor: T.primary }} />
          <span className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2" style={{ borderColor: T.primary }} />
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${T.primary}55, transparent 70%)`, filter: "blur(20px)" }} />

          <div className="relative flex flex-wrap items-end justify-between gap-5">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-sm flex items-center justify-center"
                  style={{ background: "rgba(255,255,255,0.12)", border: "1.5px solid rgba(255,255,255,0.2)", color: T.primary }}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="font-mono text-[10px] tracking-[0.3em] opacity-80">// {meta.fullName.toUpperCase()}</div>
              </div>
              <h1 className="font-display font-bold text-4xl md:text-5xl leading-none">{meta.name}</h1>
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="px-3 py-1 rounded-sm text-xs font-mono tracking-wider"
                  style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.15)" }}>
                  {meta.bands}
                </span>
                <span className="px-3 py-1 rounded-sm text-xs font-mono tracking-wider"
                  style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.15)" }}>
                  {meta.duration}
                </span>
                <span className="px-3 py-1 rounded-sm text-xs font-mono tracking-wider"
                  style={{ background: T.primary, color: "#fff" }}>
                  AI · WHISPER · GPT-4o
                </span>
              </div>
            </div>

            {/* Skills tick list */}
            <div className="flex flex-wrap gap-2" data-testid="exam-skills-checklist">
              {meta.skills.map((s) => {
                const SI = SKILL_ICON[s] || BookOpen;
                return (
                  <div key={s} className="inline-flex items-center gap-2 px-3 py-2 rounded-sm"
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)" }}>
                    <div className="w-5 h-5 rounded-sm flex items-center justify-center"
                      style={{ background: T.primary }}>
                      <SI className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase">{s}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Heading */}
        <div className="mb-6">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>
            // CHOOSE YOUR PRACTICE MODE
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-1.5" style={{ color: T.text, letterSpacing: "-0.02em" }}>
            How would you like to practise today?
          </h2>
          <p className="text-sm md:text-base mt-1.5" style={{ color: T.textMuted }}>
            Pick guided AI teaching (hints, corrections, voice support) or jump straight into a strict mock test simulation.
          </p>
        </div>

        {/* Two big choice cards */}
        <div className="grid md:grid-cols-2 gap-5">
          {/* AI Guidelines card */}
          <button
            onMouseEnter={() => setHover("ai")}
            onMouseLeave={() => setHover(null)}
            onClick={() => go(meta.routes.ai)}
            data-testid="choice-ai-guidelines"
            className="group relative overflow-hidden rounded-sm text-left transition-all duration-300 hover:-translate-y-1"
            style={{
              background: T.cardBg,
              border: `1.5px solid ${hover === "ai" ? T.primary : T.border}`,
              minHeight: 360,
              boxShadow: hover === "ai"
                ? `0 22px 50px -28px ${T.primaryGlow}, 0 0 24px ${T.primaryGlow}`
                : "0 10px 28px -22px rgba(0,48,73,0.18)",
            }}
          >
            {/* corner brackets */}
            <span className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: T.primary }} />
            <span className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: T.primary }} />
            <span className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: T.primary }} />
            <span className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: T.primary }} />
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${T.primary}25, transparent 70%)`, filter: "blur(20px)" }} />

            <div className="relative p-7 md:p-9 h-full flex flex-col">
              <div className="flex items-center justify-between mb-5">
                <div className="w-14 h-14 rounded-sm flex items-center justify-center"
                  style={{ background: T.primary, color: "#fff", boxShadow: `0 0 16px ${T.primaryGlow}` }}>
                  <Sparkles className="w-7 h-7" />
                </div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>
                  MODE · A
                </div>
              </div>

              <div className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: T.textMuted }}>
                Interactive Teaching
              </div>
              <div className="font-display font-bold text-3xl md:text-[34px] leading-tight mt-1" style={{ color: T.text }}>
                Practice with<br />
                <span style={{ color: T.primary, textShadow: `0 0 18px ${T.primaryGlow}` }}>AI Guidelines</span>
              </div>

              <p className="text-sm md:text-base mt-3 leading-relaxed" style={{ color: T.textMuted }}>
                {meta.aiBlurb}
              </p>

              <div className="mt-5 space-y-2">
                {[
                  { Icon: Brain, label: "Stage-by-stage explanation" },
                  { Icon: Volume2, label: "Text hints by default · voice on demand" },
                  { Icon: ShieldCheck, label: "Instant corrections after each task" },
                ].map(({ Icon: I, label }, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-xs md:text-sm" style={{ color: T.text }}>
                    <div className="w-5 h-5 rounded-sm flex items-center justify-center"
                      style={{ background: `${T.primary}1A`, color: T.primary }}>
                      <I className="w-3.5 h-3.5" />
                    </div>
                    {label}
                  </div>
                ))}
              </div>

              <div className="mt-auto pt-6 inline-flex items-center gap-2 font-display font-bold tracking-[0.18em] text-sm"
                style={{ color: T.primary }}>
                BEGIN GUIDED LEARNING
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>

          {/* Mock test card */}
          <button
            onMouseEnter={() => setHover("mock")}
            onMouseLeave={() => setHover(null)}
            onClick={() => go(meta.routes.mock)}
            data-testid="choice-mock-test"
            className="group relative overflow-hidden rounded-sm text-left transition-all duration-300 hover:-translate-y-1"
            style={{
              background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
              color: "#fff",
              minHeight: 360,
              boxShadow: hover === "mock"
                ? `0 22px 50px -28px ${T.primaryGlow}, 0 0 30px ${T.primaryGlow}`
                : `0 16px 40px -24px ${T.primary}cc`,
            }}
          >
            {/* scanlines */}
            <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
              background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 3px)"
            }} />
            <span className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-white/70" />
            <span className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-white/70" />
            <span className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-white/70" />
            <span className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-white/70" />

            <div className="relative p-7 md:p-9 h-full flex flex-col">
              <div className="flex items-center justify-between mb-5">
                <div className="w-14 h-14 rounded-sm flex items-center justify-center"
                  style={{ background: "rgba(255,255,255,0.18)", border: "1.5px solid rgba(255,255,255,0.3)" }}>
                  <Gauge className="w-7 h-7 text-white" />
                </div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-90">MODE · B</div>
              </div>

              <div className="font-mono text-[10px] tracking-[0.25em] uppercase opacity-80">
                Strict Exam Simulation
              </div>
              <div className="font-display font-bold text-3xl md:text-[34px] leading-tight mt-1">
                Practice<br />
                <span style={{ textShadow: "0 0 24px rgba(255,255,255,0.55)" }}>Mock Test</span>
              </div>

              <p className="text-sm md:text-base mt-3 leading-relaxed opacity-90">
                {meta.mockBlurb}
              </p>

              <div className="mt-5 space-y-2">
                {[
                  { Icon: Timer, label: "Real exam timing · no pause · auto-submit" },
                  { Icon: Target, label: "Full AI grading with band/score breakdown" },
                  { Icon: ShieldCheck, label: "Identical to real test conditions" },
                ].map(({ Icon: I, label }, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-xs md:text-sm">
                    <div className="w-5 h-5 rounded-sm flex items-center justify-center"
                      style={{ background: "rgba(255,255,255,0.18)" }}>
                      <I className="w-3.5 h-3.5" />
                    </div>
                    {label}
                  </div>
                ))}
              </div>

              <div className="mt-auto pt-6 inline-flex items-center gap-2 font-display font-bold tracking-[0.18em] text-sm">
                START MOCK TEST
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>
        </div>

        {/* Footer help */}
        <div className="mt-8 text-center text-xs font-mono tracking-[0.25em] uppercase" style={{ color: T.textMuted }}>
          NOT SURE? PICK GUIDED LEARNING FIRST — IT'S FREE FOR YOUR FIRST 3 SESSIONS.
        </div>
      </div>
    </DashboardLayout>
  );
}
